import { Box, Typography } from "@mui/material";
import { cohortStaticData } from "../../utils/CommonUtils/constants";
import upArrowIcon from "../../assets/up-arrow.svg";
import downArrowIcon from "../../assets/down-arrow.svg";
import baselineNotCompletedImg from "../../assets/baseline_notcompleted_girl.svg";
import "./CohortProgressComparison.css";
import useTranslationSetup from "../../utils/useTranslationSetup";

const COHORT_COLORS = {
  1: { solid: "#ff649b", light: "#FFF1F7" },
  2: { solid: "#ffb484", light: "#FFF1E8" },
  3: { solid: "#5c8dff", light: "#EEF3FF" },
  4: { solid: "#66cb00", light: "#F4FFE9" },
};

const BAR_WIDTH = 80;
const BAR_GAP = 0;
const BAR_STRIDE = BAR_WIDTH + BAR_GAP;
const BAR_HEIGHTS = [115, 155, 195, 235];
const CHART_HEIGHT = 270;
const CHART_WIDTH = 3 * BAR_STRIDE + BAR_WIDTH;

const getBarLeft = (idx) => idx * BAR_STRIDE;
const getBarCenterX = (idx) => getBarLeft(idx) + BAR_WIDTH / 2;
const getBarTopY = (idx) => CHART_HEIGHT - BAR_HEIGHTS[idx];

const getDirectionConfig = (cohortDiff, cwpmChange, t) => {
  let badgeColor, badgeText, badgeIcon;
  if (cohortDiff > 0) {
    badgeColor = "#2EAF00";
    badgeText = t("cohort_progress_badge_text_up", { count: Math.abs(cohortDiff) });
    badgeIcon = "↑";
  } else if (cohortDiff < 0) {
    badgeColor = "#CA3500";
    badgeText = t("cohort_progress_badge_text_down", { count: Math.abs(cohortDiff) });
    badgeIcon = "↓";
  } else {
    badgeColor = "#1447E6";
    badgeText = cwpmChange > 0 ? t("cohort_progress_badge_text_improved") : t("cohort_progress_badge_text_same");
    badgeIcon = "—";
  }

  let accentColor;
  if (cohortDiff > 0 || (cohortDiff === 0 && cwpmChange > 0)) {
    accentColor = "#2EAF00";
  } else if (cohortDiff < 0 || (cohortDiff === 0 && cwpmChange < 0)) {
    accentColor = "#CA3500";
  } else {
    accentColor = "#1447E6";
  }

  return { badgeColor, badgeText, badgeIcon, accentColor };
};

const isBarActive = (barCohort, previousCohort, currentCohort) => {
  const lo = Math.min(previousCohort, currentCohort);
  const hi = Math.max(previousCohort, currentCohort);
  return barCohort >= lo && barCohort <= hi;
};

const getDottedCurve = (i) => {
  const p0 = { x: getBarCenterX(i), y: getBarTopY(i) };
  const p2 = { x: getBarCenterX(i + 1), y: getBarTopY(i + 1) };
  const p1 = { x: (p0.x + p2.x) / 2, y: Math.min(p0.y, p2.y) - 35 };
  return { p0, p1, p2 };
};

const evalBezier = (t, p0, p1, p2) => ({
  x: (1 - t) * (1 - t) * p0.x + 2 * (1 - t) * t * p1.x + t * t * p2.x,
  y: (1 - t) * (1 - t) * p0.y + 2 * (1 - t) * t * p1.y + t * t * p2.y,
});

const ARROW_LEN = 12;
const ARROW_HALF_W = 5.5;

const arrowedPath = (cp0, cp1, cp2, color) => {
  const chord = Math.sqrt((cp2.x - cp0.x) ** 2 + (cp2.y - cp0.y) ** 2);
  const tStop = Math.max(0.5, 1 - ARROW_LEN / chord);

  const splitCtrl = {
    x: (1 - tStop) * cp0.x + tStop * cp1.x,
    y: (1 - tStop) * cp0.y + tStop * cp1.y,
  };
  const basePt = evalBezier(tStop, cp0, cp1, cp2);

  const dx = cp2.x - basePt.x;
  const dy = cp2.y - basePt.y;
  const len = Math.sqrt(dx * dx + dy * dy);
  const ux = dx / len;
  const uy = dy / len;
  const px = -uy;
  const py = ux;

  const ax = basePt.x + px * ARROW_HALF_W;
  const ay = basePt.y + py * ARROW_HALF_W;
  const bx = basePt.x - px * ARROW_HALF_W;
  const by = basePt.y - py * ARROW_HALF_W;

  return (
    <>
      <path
        d={`M ${cp0.x} ${cp0.y} Q ${splitCtrl.x} ${splitCtrl.y} ${basePt.x} ${basePt.y}`}
        fill="none"
        stroke={color}
        strokeWidth="3"
        strokeLinecap="round"
      />
      <polygon points={`${cp2.x},${cp2.y} ${ax},${ay} ${bx},${by}`} fill={color} />
    </>
  );
};

const ChartSVG = ({ previousCohort, currentCohort, direction, cwpmChange }) => {
  const cohortDiff = currentCohort - previousCohort;
  const prevIdx = previousCohort - 1;
  const currIdx = currentCohort - 1;

  const dottedLines = [];
  for (let i = 0; i < 3; i++) {
    const { p0, p1, p2 } = getDottedCurve(i);
    dottedLines.push(
      <path
        key={i}
        d={`M ${p0.x} ${p0.y} Q ${p1.x} ${p1.y} ${p2.x} ${p2.y}`}
        fill="none"
        stroke="#d4d4d4"
        strokeWidth="1.5"
        strokeDasharray="6 5"
      />
    );
  }

  let mainArrow = null;

  if (Math.abs(cohortDiff) === 1) {
    const loIdx = Math.min(prevIdx, currIdx);
    const { p0, p1, p2 } = getDottedCurve(loIdx);

    if (cohortDiff === 1) {
      mainArrow = arrowedPath(p0, p1, p2, direction.accentColor);
    } else {
      mainArrow = arrowedPath(p2, p1, p0, direction.accentColor);
    }
  } else if (Math.abs(cohortDiff) > 1) {
    const from = { x: getBarCenterX(prevIdx), y: getBarTopY(prevIdx) };
    const to = { x: getBarCenterX(currIdx), y: getBarTopY(currIdx) };
    const cp = { x: (from.x + to.x) / 2, y: Math.min(from.y, to.y) - 55 };

    mainArrow = arrowedPath(from, cp, to, direction.accentColor);
  } else if (cwpmChange !== 0) {
    const lineIdx = currIdx < 3 ? currIdx : currIdx - 1;
    const { p0, p1, p2 } = getDottedCurve(lineIdx);
    const barIsLower = currIdx === lineIdx;
    const partialT = 0.4;

    if (barIsLower) {
      const tip = evalBezier(partialT, p0, p1, p2);
      const q1 = {
        x: (1 - partialT) * p0.x + partialT * p1.x,
        y: (1 - partialT) * p0.y + partialT * p1.y,
      };

      if (cwpmChange > 0) {
        mainArrow = arrowedPath(p0, q1, tip, direction.accentColor);
      } else {
        mainArrow = arrowedPath(tip, q1, p0, direction.accentColor);
      }
    } else {
      const tip = evalBezier(1 - partialT, p0, p1, p2);
      const r1 = {
        x: (1 - (1 - partialT)) * p1.x + (1 - partialT) * p2.x,
        y: (1 - (1 - partialT)) * p1.y + (1 - partialT) * p2.y,
      };

      if (cwpmChange > 0) {
        mainArrow = arrowedPath(tip, r1, p2, direction.accentColor);
      } else {
        mainArrow = arrowedPath(p2, r1, tip, direction.accentColor);
      }
    }
  } else {
    const lineIdx = currIdx < 3 ? currIdx : currIdx - 1;
    const { p0, p1, p2 } = getDottedCurve(lineIdx);
    const barIsLower = currIdx === lineIdx;
    const partialT = barIsLower ? 0.25 : 0.75;
    const dot = evalBezier(partialT, p0, p1, p2);
    const base = barIsLower ? p0 : p2;
    const q1 = barIsLower
      ? {
          x: (1 - partialT) * p0.x + partialT * p1.x,
          y: (1 - partialT) * p0.y + partialT * p1.y,
        }
      : {
        x: (1 - partialT) * p1.x + partialT * p2.x,
        y: (1 - partialT) * p1.y + partialT * p2.y,
        };

    mainArrow = (
      <>
        <path
          d={`M ${base.x} ${base.y} Q ${q1.x} ${q1.y} ${dot.x} ${dot.y}`}
          fill="none"
          stroke={direction.accentColor}
          strokeWidth="3"
          strokeLinecap="round"
        />
        <circle cx={dot.x} cy={dot.y} r={6} fill={direction.accentColor} />
      </>
    );
  }

  return (
    <svg
      className="cohort-progress-svg"
      width={CHART_WIDTH}
      height={CHART_HEIGHT}
      viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
    >
      {dottedLines}
      {mainArrow}
    </svg>
  );
};

const CohortProgressComparison = ({
  previousCohort,
  currentCohort,
  previousCwpm,
  currentCwpm,
}) => {
  const noPreviousAssessment = previousCohort == null || previousCwpm == null;
  const { t } = useTranslationSetup();
  if (noPreviousAssessment) {
    return (
      <Box className="cohort-progress-wrapper">
        <Typography className="cohort-progress-heading">
          {t("compare_previous_current")}
        </Typography>
        <Box className="cohort-progress-container cohort-no-baseline">
          <Box>
          <Typography className="cohort-no-baseline-title">
            {t("baseline_assessment_not_completed")}
          </Typography>
          <Typography className="cohort-no-baseline-subtitle">
            {t("baseline_assessment_not_completed_description")}
          </Typography>
          </Box>
          <img
            src={baselineNotCompletedImg}
            alt="Baseline not completed"
            className="cohort-no-baseline-img"
          />
        </Box>
      </Box>
    );
  }

  const cohortDiff = currentCohort - previousCohort;
  const cwpmChange = currentCwpm - previousCwpm;
  const direction = getDirectionConfig(cohortDiff, cwpmChange, t);
  const sameCohort = previousCohort === currentCohort;

  return (
    <Box className="cohort-progress-wrapper">
      <Typography className="cohort-progress-heading">
        {t("compare_previous_current")}
      </Typography>

      <Box className="cohort-progress-container">
        <Box
          className="cohort-progress-badge"
          style={{ borderColor: direction.badgeColor, color: direction.badgeColor }}
        >
          <span className="cohort-progress-badge-icon">
            {direction.badgeIcon}
          </span>
          <span className="cohort-progress-badge-text">
            {direction.badgeText}
          </span>
        </Box>

        <Box
          className="cohort-progress-chart"
          style={{ width: CHART_WIDTH, height: CHART_HEIGHT }}
        >
          <ChartSVG
            previousCohort={previousCohort}
            currentCohort={currentCohort}
            direction={direction}
            cwpmChange={cwpmChange}
          />

          {[1, 2, 3, 4].map((num) => {
            const idx = num - 1;
            const active = isBarActive(num, previousCohort, currentCohort);
            const colors = COHORT_COLORS[num];
            const data = cohortStaticData[num];

            return (
              <Box
                key={num}
                className="cohort-progress-bar"
                style={{
                  left: getBarLeft(idx),
                  width: BAR_WIDTH,
                  height: BAR_HEIGHTS[idx],
                  bottom: 0,
                  // zIndex: num,
                  backgroundColor: active ? colors.solid : "#ffffff",
                  borderColor: active ? colors.solid : colors.solid,
                  borderWidth: active ? "2px" : "1.5px",
                  borderStyle: "solid",
                  borderRadius: "10px 10px 0 0",
                }}
              >
                <Box
                  className="cohort-progress-bar-icon-wrap"
                  style={{
                    backgroundColor: active
                      ? "#ffffff"
                      : colors.light,
                  }}
                >
                  <img
                    src={data.icon}
                    alt={data.title}
                    className="cohort-progress-bar-icon"
                  />
                </Box>
                <Typography
                  className={`cohort-progress-bar-label ${active ? "active" : "inactive"}`}
                >
                  {data.title}
                  {/* <span className="cohort-progress-bar-code">
                    (C{num})
                  </span> */}
                </Typography>
              </Box>
            );
          })}
        </Box>

        <Box
          className="cohort-progress-scores"
          style={{ width: CHART_WIDTH }}
        >
          {sameCohort ? (
            <Box
              className="cohort-progress-score-pair"
              style={{
                left: getBarLeft(previousCohort - 1),
                width: BAR_WIDTH,
              }}
            >
              <Typography className="cohort-progress-score-prev">
                {previousCwpm}
              </Typography>
              <Typography
                className="cohort-progress-score-curr"
                style={{ color: direction.accentColor }}
              >
                {currentCwpm}
              </Typography>
            </Box>
          ) : (
            <>
              <Box
                className="cohort-progress-score-single"
                style={{
                  left: getBarLeft(previousCohort - 1),
                  width: BAR_WIDTH,
                }}
              >
                <Typography className="cohort-progress-score-prev">
                  {previousCwpm}
                </Typography>
              </Box>
              <Box
                className="cohort-progress-score-single"
                style={{
                  left: getBarLeft(currentCohort - 1),
                  width: BAR_WIDTH,
                }}
              >
                <Typography
                  className="cohort-progress-score-curr"
                  style={{ color: direction.accentColor }}
                >
                  {currentCwpm}
                </Typography>
              </Box>
            </>
          )}
        </Box>

        <Box className="cohort-progress-legend">
          <Box className="cohort-progress-legend-pill">
            <span
              className="cohort-progress-legend-dot"
              style={{ borderColor: "#9e9e9e" }}
            >
              <span
                className="cohort-progress-legend-dot-inner"
                style={{ backgroundColor: "#9e9e9e" }}
              />
            </span>
            <Typography className="cohort-progress-legend-label">
              {t("previous")}
            </Typography>
          </Box>
          <Box
            className="cohort-progress-legend-pill"
            style={{ borderColor: direction.accentColor }}
          >
            <span
              className="cohort-progress-legend-dot"
              style={{ borderColor: direction.accentColor }}
            >
              <span
                className="cohort-progress-legend-dot-inner"
                style={{ backgroundColor: direction.accentColor }}
              />
            </span>
            <Typography
              className="cohort-progress-legend-label"
              style={{ color: direction.accentColor }}
            >
              {t("current")}
            </Typography>
          </Box>
        </Box>

        {!(cohortDiff === 0 && cwpmChange === 0) && (
          <Box className="cohort-progress-cwpm-footer">
            <Typography className="cohort-progress-cwpm-label">
              {t("cwpm_change_label")}
            </Typography>
            <Box className="cohort-progress-cwpm-val">
              <Typography
                className="cohort-progress-cwpm-number"
              >
                {Math.abs(cwpmChange)}
              </Typography>
              <img
                src={cwpmChange >= 0 ? upArrowIcon : downArrowIcon}
                alt={cwpmChange >= 0 ? "up" : "down"}
                className="cohort-progress-cwpm-trend-icon"
              />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CohortProgressComparison;
